import { EmployeeService } from './services/employee.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ICadastroEmployee, IListByFilterEmployee, IExportExcelFilterEmployee } from './models/employee';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';
import { CompanyService } from '../../companies/services/company.service';
import { ICompany } from '../../companies/models/company.interface';
import { of, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { EMPLOYEE_STATUS_INFO } from 'src/app/shared/data/employee-status.enum';

@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss']
})
export class EmployeeComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private listagemSub?: Subscription;

  ERoleAdminString = ERoleAdminString;
  acaoInput = new UntypedFormControl();
  ordenarControl = new UntypedFormControl(1);
  companyControl = new UntypedFormControl();
  statusControl = new UntypedFormControl();
  employers: ICadastroEmployee[] = [];
  companies: ICompany[] = [];
  opcoesStatus = EMPLOYEE_STATUS_INFO;
  formDisplayed = '';
  /** Termo da última busca feita, para o contador "N resultados para ..." (OE-1808). */
  termoBuscado = '';
  opcoesOrdenacao = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    },
    {
      value: EOrdenacao.MaisNovos,
      label: 'Mais novos'
    },
    {
      value: EOrdenacao.MaisAntigos,
      label: 'Mais antigos'
    }
  ];
  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  constructor(
    private readonly employeeservice: EmployeeService,
    private readonly sessionService: SessionService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService
  ) {}

  /** Quem está preso a uma empresa não escolhe empresa no filtro (mesma regra da lista de filiais). */
  get empresaDaSessao(): string | undefined {
    return this.sessionService.getSession().companyId;
  }

  ngOnInit(): void {
    this.carregarColaborador();
    this.carregarEmpresas();
    this.ordenarControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((ordenacaoSelecionada: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.carregarColaborador();
    });
    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dadosPaginacao.paginaSelecionada = 0;
      this.carregarColaborador();
    });
    this.statusControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dadosPaginacao.paginaSelecionada = 0;
      this.carregarColaborador();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarEmpresas(): void {
    this.companyService
      .listarEmpresasAtivas()
      .pipe(takeUntil(this.destroy$))
      .subscribe((resposta) => {
        this.companies = resposta.data;
      });
  }

  filtrarColaborador(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.carregarColaborador();
  }

  limparFiltros(): void {
    this.acaoInput.reset();
    this.companyControl.reset(null, { emitEvent: false });
    this.statusControl.reset(null, { emitEvent: false });
    this.carregarColaborador();
  }

  /** Tira só o termo, sem mexer nos outros filtros (OE-1808). */
  limparBusca(): void {
    this.acaoInput.reset();
    this.filtrarColaborador();
  }

  carregarColaborador(): void {
    const partnerId = this.sessionService.getSession().partnerId;
    // LGPD: empresa da sessao sempre prevalece; dropdown so filtra quando a sessao nao esta restrita a uma empresa (Admin/Manager).
    const companyId = this.sessionService.getSession().companyId || this.companyControl.value;
    const accountRole = this.sessionService.getSession().accountRole;

    const filtros: IListByFilterEmployee = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    // Nome só com espaços conta como busca vazia (OE-1808).
    this.termoBuscado = String(this.acaoInput.value ?? '').trim();
    if (this.termoBuscado) {
      filtros.searchText = this.termoBuscado;
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (this.statusControl.value !== null && this.statusControl.value !== undefined) {
      filtros.status = Number(this.statusControl.value);
    }

    // Filtros trocados em sequência: cancela a busca anterior para a resposta antiga não sobrescrever a nova.
    this.listagemSub?.unsubscribe();
    this.listagemSub = this.employeeservice
      .listarTodosColaboradores(filtros)
      .pipe(
        // Excluir o único item da última página deixaria a lista vazia com colaboradores nas anteriores.
        switchMap((res) => {
          if (res.data?.length || this.dadosPaginacao.paginaSelecionada <= 0) return of(res);
          this.dadosPaginacao.paginaSelecionada--;
          return this.employeeservice.listarTodosColaboradores({
            ...filtros,
            pageNumber: Number(this.dadosPaginacao.paginaSelecionada)
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => {
          this.employers = res.data;
          this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
          this.dadosPaginacao.itensTotal = res.count!;
        },
        // Mantém a lista atual; o interceptor já avisa 403 e erro com reason.
        error: (erro) => {
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Erro ao carregar os colaboradores. Tente novamente.', EMessageType.Error);
          }
        }
      });
  }

  changeForm(formType: string): void {
    this.formDisplayed = formType;
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;

    this.carregarColaborador();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.dadosPaginacao.paginaSelecionada = 0;

    this.carregarColaborador();
  }

  public downloadExcel(): void {
    const partnerId = this.sessionService.getSession().partnerId;
    const companyId = this.sessionService.getSession().companyId || this.companyControl.value;
    const accountRole = this.sessionService.getSession().accountRole;

    const filtros: IExportExcelFilterEmployee = {};

    if (this.acaoInput.value) {
      filtros.searchText = String(this.acaoInput.value);
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (this.statusControl.value !== null && this.statusControl.value !== undefined) {
      filtros.status = Number(this.statusControl.value);
    }

    this.employeeservice.exportarExcel(filtros);
  }
}
